import type { ReactElement } from 'react';
import { lazy, Suspense, useState } from 'react';

import BpkButton, { BUTTON_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-button';

import { ELEMENT_NAME, EVENT_TYPE_EVENT_FIELD } from '../constants';
import { getEvent } from '../userTrackingEvents';

import type { CultureData } from '../Footer';
import type { ProductVertical } from '../constants';
import type { CultureSelectorFooterEmbeddedProps } from '../types';
import type { OnCultureSelectorButtonClick } from '@web-platform/global-components-culture-selector/helpers/useCultureSelectorData';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

import STYLES from './CultureSelectorButton.module.scss';

const LazyCultureSelector = lazy(() => import('@web-platform/global-components-culture-selector'));

type CultureSelectorButtonProps = {
  cultureSelectorProps?: CultureSelectorFooterEmbeddedProps;
  isFetching?: boolean;
  onCultureSelectorButtonClick: OnCultureSelectorButtonClick;
  logger: Logger;
  cultureData: CultureData;
  trackEventFn?: TrackingEventFn;
  osName?: string;
  pageType: string;
  vertical: ProductVertical;
};

const CultureSelectorButton = ({
  cultureData,
  cultureSelectorProps,
  isFetching,
  logger,
  onCultureSelectorButtonClick,
  osName = '',
  pageType,
  trackEventFn,
  vertical,
}: CultureSelectorButtonProps): ReactElement => {
  const [isCultureSelectorOpen, setIsCultureSelectorOpen] = useState(false);
  const localeNativeShortName = cultureData.localeNativeShortName || 'English (UK)';
  const marketTranslatedName = cultureData.marketTranslatedName || 'United Kingdom';
  const symbol = cultureData.currencyData?.symbol || '£';
  const currency = cultureData.currencyData?.currency || 'GBP';
  const locale = cultureData.languageShort;
  const { country } = cultureData;

  const currencyString = (
    <>
      <span>{symbol}&nbsp;</span>
      <span>{currency}</span>
    </>
  );
  const cultureButtonItemSeparator = (
    <span className={STYLES['culture-selector-separator']}>·</span>
  );
  const currencyElement = <span className={STYLES['currency-label']}>{currencyString}</span>;

  return (
    <>
      <BpkButton
        type={BUTTON_TYPES.secondaryOnDark}
        onClick={(e) => {
          e.preventDefault();
          trackEventFn &&
            trackEventFn([
              getEvent({
                eventType: EVENT_TYPE_EVENT_FIELD.EVENT_TYPE_CLICK,
                elementName: ELEMENT_NAME.CULTURE_BUTTON,
                country,
                locale,
                currency,
                osName,
                pageType,
                vertical,
              }),
            ]);
          setIsCultureSelectorOpen((prevState) => !prevState);
          onCultureSelectorButtonClick()
            .then(() => {
              setIsCultureSelectorOpen(true);
            })
            .catch((error) => {
              logger.logError(error);
              setIsCultureSelectorOpen(false);
            });
        }}
        className={STYLES.button}
        fullWidth
      >
        <span className={STYLES['culture-selector-text']}>
          {marketTranslatedName}
          {cultureButtonItemSeparator}
          {localeNativeShortName}
          {cultureButtonItemSeparator}
          {currencyElement}
        </span>
      </BpkButton>
      {cultureSelectorProps && isCultureSelectorOpen ? (
        <Suspense fallback={null}>
          <LazyCultureSelector
            isOpen={isCultureSelectorOpen && !isFetching}
            onClose={() => setIsCultureSelectorOpen((prevState) => !prevState)}
            logger={logger}
            currency={currency}
            locale={locale}
            market={country}
            trackEventFn={trackEventFn}
            {...cultureSelectorProps}
          />
        </Suspense>
      ) : undefined}
    </>
  );
};

export default CultureSelectorButton;
